import styles from "../styles/main.module.css";
import PremiumHandler from "../components/PremiumHandler";
import SimpleConfig from "../components/SimpleConfig";
import AdvancedConfig from "../components/AdvancedConfig";

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BACKEND_URL } from "../config";

const Main = () => {
    const [revisions, setRevisions] = useState([]);
    const [selectedRevision, setSelectedRevision] = useState("");
    const [activeTab, setActiveTab] = useState("simpleConfig"); 
    const [isPremium, setIsPremium] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [quotaErrorMessage, setQuotaErrorMessage] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // Définir le titre de la page
        document.title = "Memory Curve";

        // Récupérer les paramètres depuis l'URL
        const queryParams = new URLSearchParams(location.search);
        const urlUserId = queryParams.get("user_id");
        const calendarAuth = queryParams.get("calendar_auth");
        const errorReason = queryParams.get("reason");
        
        // Traiter les erreurs d'autorisation du calendrier
        if (calendarAuth === "error") {
            navigate("/main", { replace: true });
            
            // Afficher un message d'erreur adapté
            switch (errorReason) {
                case "token_exchange":
                    alert("Une erreur s'est produite lors de l'autorisation Google Calendar. Veuillez réessayer.");
                    break;
                case "user_not_found":
                    alert("Utilisateur non trouvé. Veuillez vous reconnecter.");
                    sessionStorage.clear();
                    navigate("/login");
                    return;
                case "invalid_state":
                    alert("Session expirée. Veuillez réessayer.");
                    break;
                default:
                    alert("Une erreur inattendue s'est produite. Veuillez réessayer plus tard.");
            }
            
            // Si une autorisation de calendrier était en attente, la nettoyer
            if (sessionStorage.getItem("calendar_auth_pending")) {
                sessionStorage.removeItem("calendar_auth_pending");
            }
        }
        
        // Traiter le succès de l'autorisation du calendrier
        else if (calendarAuth === "success") {
            // Nettoyer l'URL et afficher un message de succès
            navigate("/main", { replace: true });
            
            // Vérifier si une action était en attente avant la redirection
            if (sessionStorage.getItem("calendar_auth_pending")) {
                sessionStorage.removeItem("calendar_auth_pending");
                alert("Google Calendar authorization successful! Your revisions will now be synchronized with your calendar.");
                
                // Recharger les révisions pour s'assurer que tout est à jour
                const userId = sessionStorage.getItem("user_id");
                if (userId) {
                    fetch(`${BACKEND_URL}/user-revisions/${userId}`)
                        .then((response) => response.json())
                        .then((data) => setRevisions(data.revisions || []))
                        .catch((error) => console.error("Error fetching revisions:", error));
                }
            }
        }
        
        // Si on a un user_id dans l'URL, le stocker dans sessionStorage
        if (urlUserId) {
            sessionStorage.setItem("user_id", urlUserId);
            // Nettoyer l'URL pour éviter les problèmes de rafraîchissement
            navigate("/main", { replace: true });
        }

        const userId = sessionStorage.getItem("user_id");
        if (!userId) {
            alert("Please log in first.");
            navigate("/login");
            return;
        }

        //Fonction pour récupérer les révisions et vérifier le statut premium
        const fetchData = async () => {
            try {
                // Récupération des révisions
                const responseRevisions = await fetch(`${BACKEND_URL}/user-revisions/${userId}`);
                const dataRevisions = await responseRevisions.json();

                if (dataRevisions.revisions) {
                    console.log("Revisions loaded:", dataRevisions.revisions);
                    setRevisions(dataRevisions.revisions);
                } else {
                    console.warn("No revisions found.");
                }

                // Vérification du statut premium
                const responseUser = await fetch(`${BACKEND_URL}/user/${userId}`);
                const dataUser = await responseUser.json();
                
                if (dataUser.is_premium) {
                    sessionStorage.setItem("is_premium", "true"); // Mise à jour sessionStorage
                    setIsPremium(true);
                } else {
                    sessionStorage.setItem("is_premium", "false");
                    setIsPremium(false);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [navigate, location]);

    const handleConsult = () => {
        if (!selectedRevision) {
            alert("Please select a revision name.");
            return;
        }
        
        const userId = sessionStorage.getItem("user_id");
        // Rediriger vers le composant CurvePage avec les bons paramètres
        navigate(`/curve?name=${encodeURIComponent(selectedRevision)}&user_id=${encodeURIComponent(userId)}`);
    };
    
    const updateRevisions = () => {
        const userId = sessionStorage.getItem("user_id");
    
        fetch(`${BACKEND_URL}/user-revisions/${userId}`)
            .then((response) => response.json())
            .then((data) => setRevisions(data.revisions || []))
            .catch((error) => console.error("Error fetching revisions:", error));
    };
    

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <button 
                    className={styles.button} 
                    onClick={() => { sessionStorage.clear(); navigate("/login"); }}
                    style={{ position: 'absolute', left: 0, top: 15, width: 'auto' }}
                >
                    ← Log out
                </button>
                <h1 className={styles.title}>Memory Curve App</h1>
            </div>

            {/* Sélection des révisions */}
            <div className={styles.formGroup}>
                <h3>Consult an existing Memory Curve:</h3>
                <select
                    value={selectedRevision}
                    onChange={(e) => setSelectedRevision(e.target.value)}
                    className={styles.select}
                >
                    <option value="">Select your revision</option>
                    {revisions.map((rev) => (
                        <option key={rev.name} value={rev.name}>
                            {rev.name}
                        </option>
                    ))}
                </select>
            </div>
            <button className={styles.button} onClick={handleConsult}>Consult</button>

            {/* Ajouter une nouvelle courbe */}
            <div className={styles.formGroup}>
                <h3>Or add a New Memory Curve</h3>

                {/* Onglets */}
                <div className={styles.tabs}>
                    <div
                        className={`${styles.tab} ${activeTab === "simpleConfig" ? styles.active : ""}`}
                        onClick={() => setActiveTab("simpleConfig")}
                    >
                        Simple Configuration
                    </div>
                    <div
                        className={`${styles.tab} ${activeTab === "advancedConfig" ? styles.active : ""}`}
                        onClick={() => {
                            const premiumStatus = sessionStorage.getItem("is_premium") === "true"; // Vérification à jour
                        
                            if (premiumStatus) {
                                setActiveTab("advancedConfig"); //Si premium, afficher direct
                            } else {
                                setQuotaErrorMessage(""); // Réinitialiser le message d'erreur
                                setShowPremiumModal(true); //Sinon, afficher la modale
                            }
                        }}
                    >
                        Advanced Configuration
                    </div>
                </div>

                {/* Contenu des onglets */}
                <div className={`${styles.tabContent} ${activeTab === "simpleConfig" ? styles.active : ""}`}>
                    <SimpleConfig 
                        updateRevisions={updateRevisions} 
                        setShowPremiumModal={setShowPremiumModal}
                        setQuotaErrorMessage={setQuotaErrorMessage}
                    />
                </div>

                {activeTab === "advancedConfig" && isPremium && (
                    <div className={`${styles.tabContent} ${activeTab === "advancedConfig" ? styles.active : ""}`}>
                        <AdvancedConfig 
                            updateRevisions={updateRevisions}
                            setShowPremiumModal={setShowPremiumModal}
                            setQuotaErrorMessage={setQuotaErrorMessage}
                        />
                    </div>
                )}
            </div>

            {/* Gestion des fonctionnalités premium */}
            <PremiumHandler 
                isPremium={isPremium} 
                setIsPremium={setIsPremium} 
                showModal={showPremiumModal} 
                setShowModal={setShowPremiumModal} 
                errorMessage={quotaErrorMessage}
            />
        </div>
    );
};

export default Main;
